<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		background-color-class="bg-color-white"
		:content-full-height="true"
	>
		<BasePlaceHolder
			:placehold="loading"
			:line-count="10"
			:section-count="3"
			:class="['pb-300', { 'container pt-100': loading }]"
		>
			<header class="bg-color-greige py-150 py-400-md">
				<div
					class="container display-flex flex-direction-column-smo align-items-center-md justify-content-space-between-md gap-50"
				>
					<div>
						<h1 class="heading-1">{{ pageTitle }}</h1>
						<div
							v-if="isAuthenticatedNotAdvisor"
							class="display-flex flex-direction-column-smo justify-content-center mt-50"
						>
							<p class="mr-50">{{ authPageDescription }}</p>
						</div>
						<p v-else class="mt-50 text-color-content-secondary line-height-150">{{ pageDescription }}</p>
					</div>
				</div>
			</header>

			<section class="container py-200 py-400-md">
				<div v-for="(category, categoryIdx) in categorizedOfferingsList" :key="categoryIdx">
					<div :class="['display-flex align-items-center gap-50', { 'pt-200 pt-400-md': categoryIdx !== 0 }]">
						<h4 class="heading-4">{{ category.name }}</h4>
						<CounterBadge
							:text="category.offerings.length"
							theme="greige"
							type-size="body-md"
							data-test="categorized-offering-count"
						/>
					</div>
					<RichTextComponent
						v-if="category.description"
						:blocks="category.description"
						class="text-color-content-secondary mt-50"
					/>
					<OfferingCard
						v-for="(offering, offeringIdx) in category.offerings"
						:key="offeringIdx"
						:offering="offering"
						trigger="browse-investments"
					/>
				</div>
			</section>
			<section class="container">
				<div class="offering-footnote eta pt-150 bt">{{ pageFootnote }}</div>
			</section>
		</BasePlaceHolder>
	</BaseLayout>
</template>
<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed, ref, Ref } from 'vue';
import { getFunds } from '@api/sanity/proxy/funds';
import CounterBadge from '@components/badges/counter-badge.vue';
import OfferingCard from '@components/offerings/offering-card.vue';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { useAppStore } from '@stores/app';
import { Fund, FundCategory } from 'types/funds';

type FundCategoryWithOfferings = FundCategory & { offerings: Array<Fund> };

const loading = ref(true);
const appStore = useAppStore();
const authPageDescription = 'Browse our available investments and invest directly in specific funds.';
const pageTitle = 'Investments';
const offeringsList: Ref<Array<Fund>> = ref([]);

const isAuthenticatedNotAdvisor = computed((): boolean => {
	return appStore.isAuthenticated && !appStore.isAdvisor;
});

const pageDescription = computed((): string => {
	const description =
		'Fundrise investors invest in our low-cost, tax-efficient funds in two ways - through an investment plan or by directly investing into an available fund.';

	return isAuthenticatedNotAdvisor.value || appStore.isAdvisor
		? description
		: `${description} Log in to see our full list of offerings.`;
});

const metaPageDescription = computed((): string => {
	return isAuthenticatedNotAdvisor.value ? authPageDescription : pageDescription.value;
});

const pageFootnote = computed((): string => {
	const footnote =
		'The Flagship Real Estate Fund, Income Real Estate Fund, and Innovation Fund are each registered under, and conduct offerings pursuant to, the Investment Company Act of 1940. The Flagship Real Estate Fund and the Income Real Estate Fund each operate as an "interval fund." The Innovation Fund operates as a "tender offer fund."';
	return appStore.isAuthenticated
		? `${footnote} All other eREITs and the Fundrise eFund may conduct offerings pursuant to Regulation A of the Securities Act of 1933.`
		: footnote;
});

const categorizedOfferingsList = computed((): Array<FundCategoryWithOfferings> => {
	const fundCategories: Array<FundCategory> = [];
	offeringsList.value.forEach((offering) => {
		if (offering.category && !fundCategories.find((category) => category._id === offering.category?._id)) {
			fundCategories.push(offering.category);
		}
	});

	return fundCategories
		.sort((a, b) => (a.priority && b.priority ? parseInt(a.priority) - parseInt(b.priority) : 0))
		.map((category) => {
			const offeringsByCategory = offeringsList.value.filter(
				(offering) => offering.category?._id === category._id
			);
			return {
				...category,
				offerings: offeringsByCategory
			};
		})
		.filter((category) => category.offerings.length);
});

async function setup(): Promise<void> {
	try {
		const offeringsData = await getFunds({
			context: appStore.isAdvisor ? 'ADVISOR_OFFERINGS' : 'STANDARD_OFFERINGS'
		});
		offeringsList.value = offeringsData.results;

		useHead({
			title: pageTitle,
			meta: [
				{ property: 'og:title', content: pageTitle },
				{ name: 'description', content: metaPageDescription.value },
				{ property: 'og:description', content: metaPageDescription.value }
			]
		});

		loading.value = false;
	} catch {}
}
setup();
</script>
<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;

.offering-footnote {
	color: token('content-tertiary');
}
</style>
